/* global paypal */
import { Controller } from '@hotwired/stimulus'
import {
  dispatchAction,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  connectPageRequirements,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { PaypalCheckout, UserData } from '../../api/models/types'
import jitsu from '../../helpers/jitsu'
import computedValues from '../../helpers/computedValues'

// @TODO: Payment failed event + analytics tracking
// @TODO: Analytics - more properties

declare global {
  interface Window {
    paypal: any
  }
}

type ButtonLayoutValue = {
  layout: string
}

type ButtonColorValue = {
  color: string
}

type ButtonShapeValue = {
  shape: string
}

type ButtonLabelValue = {
  label: string
}

type ButtonTaglineValue = {
  tagline: string
}

type ButtonSizeValue =
  | {
      size: string
    }
  | string

export default class extends Controller {
  static targets = ['container']
  declare containerTarget: Element

  static values = {
    key: String,
    successAction: Boolean,
    action: String,
    amount: Number,
    planId: String,
    isValid: Boolean,
    buttonLayout: Object as (value: ButtonLayoutValue) => ButtonLayoutValue,
    buttonColor: Object as (value: ButtonColorValue) => ButtonColorValue,
    buttonShape: Object as (value: ButtonShapeValue) => ButtonShapeValue,
    buttonLabel: Object as (value: ButtonLabelValue) => ButtonLabelValue,
    buttonTagline: Object as (value: ButtonTaglineValue) => ButtonTaglineValue,
    buttonSize: Object as (value: ButtonSizeValue) => ButtonSizeValue,
  }

  disconnectPageRequirements?: () => void
  pageId: string
  component: PaypalCheckout
  flowId: string
  pageIndex: number
  componentIndex: number
  declare keyValue: string
  declare successActionValue: boolean
  declare actionValue: string
  declare amountValue: number
  declare planIdValue: string
  declare buttonLayoutValue: ButtonLayoutValue
  declare buttonColorValue: ButtonColorValue
  declare buttonShapeValue: ButtonShapeValue
  declare buttonLabelValue: ButtonLabelValue
  declare buttonTaglineValue: ButtonTaglineValue
  declare buttonSizeValue: ButtonSizeValue
  declare userDataValue: Partial<UserData>
  flowEl: Element | null

  declare isValidValue: true

  connect() {
    this.disconnectPageRequirements = connectPageRequirements(this)
    this.setupPaypal()
    const { pageId, component, flowEl, flowId } = getComponentContext(this)
    this.pageId = pageId
    this.component = component as PaypalCheckout
    this.flowEl = flowEl
    this.flowId = (flowId as string) || ''
    lifecycleTrigger(this, 'mounted')

    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.pageId)
    const componentIndex = flow?.pages[pageIndex]?.components.findIndex(
      (component) => component.id === this.component.id
    )
    this.pageIndex = pageIndex || 0
    this.componentIndex = componentIndex || 0
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectPageRequirements === 'function') this.disconnectPageRequirements()
  }
  onSuccess(data: { subscriptionID: string }) {
    /* Update user data with value 'paid' */
    this.dispatch('update', { detail: { key: this.keyValue, value: 'paid' } })

    if (
      this.component.output_subscription_id &&
      this.component.output_subscription_id_key &&
      data &&
      data.subscriptionID
    ) {
      this.dispatch('update', {
        detail: { key: this.component.output_subscription_id_key, value: data.subscriptionID },
      })
    }
    /* Go to next page on success */

    /* 
    Kick it to the back of the event queue
    so that the update can process first, so we get most up to date user data.
    Unsure if this actually works though, may have to use a dispatch 
    but theres also no guarantee that it will happen after the update + all the recalculating 
    */
    setTimeout(() => {
      if (!doNotStoreComponentDataInCloud(this.component)) {
        jitsu.track('payment:completed', { flow_id: this.flowId, payment_service: 'paypal' })
      }

      window.Savvy.addEventLogs?.([
        {
          flow_id: this.flowId,
          event_type: 'payment:completed',
          timestamp: Date.now(),
          user_data_snapshot: this.userDataValue as UserData,
          data: {
            payment_service: 'paypal',
            component_id: this.component.id,
            component_key: this.component.key,
            component_path: [
              ...(this.pageId ? ['pages', `${this.pageIndex}`] : []),
              'components',
              `${this.componentIndex}`,
            ],
          },
        },
      ])
      const actions = this.component.on_payment_complete_outputs
      if (Array.isArray(actions)) actions.forEach((k) => dispatchAction(this, k))
      if (
        this.component.on_payment_complete_conversions &&
        Array.isArray(this.component.on_payment_complete_conversions)
      ) {
        dispatchAction(
          this,
          this.component.on_payment_complete_conversions.map((c) => ({
            ...c,
            output: 'conversion',
          }))
        )
      }
    }, 0)

    if (this.successActionValue) this.dispatch('next', { detail: { value: this.pageId } })
    else {
      const successMessageDiv = document.createElement('div')
      successMessageDiv.innerHTML = 'Thank you for your payment!'
      this.element.appendChild(successMessageDiv)
    }

    //* Trigger the on_payment_complete_outputs outputs
    if (this.component.on_payment_complete_outputs) {
      this.dispatch('actions', {
        detail: { actions: this.component.on_payment_complete_outputs, component: this.component },
      })
    }
  }
  setupHostElement() {}
  setupPaypal() {
    /* Get payer details from somewhere somehow */
    const payer = {}
    const mountId = this.containerTarget.id

    const style = {
      layout: this.buttonLayoutValue?.layout || 'vertical',
      color: this.buttonColorValue?.color || 'gold',
      shape: this.buttonShapeValue?.shape || 'rect',
      label: this.buttonLabelValue?.label || 'paypal',
      tagline: this.buttonTaglineValue?.tagline || 'false',
      size: this.buttonSizeValue || 'auto',
    }
    if (this.buttonSizeValue && this.buttonSizeValue !== 'auto') {
      style.size = this.buttonSizeValue
    }

    if (!window.paypal) {
      console.error('Paypal script not found on page')
      return
    }

    const self = this

    window.paypal
      .Buttons({
        style,
        // Sets up the transaction when a payment button is clicked
        createOrder:
          this.actionValue === 'one-time-payment'
            ? async (
                data: string,
                actions: {
                  order: {
                    create: (arg0: {
                      purchase_units: { amount: { value: string } }[]
                      payer: {} | undefined
                    }) => Promise<{
                      id: string
                      status: string
                      links: {
                        href: string
                        rel: string
                        method: string
                      }[]
                    }>
                  }
                }
              ) => {
                try {
                  //* First, run regular page validation if enforce_validation is true
                  //* If invalid, trigger the regular validation procedure and stop here
                  if (this.component.enforce_validation && !this.isValidValue) {
                    self.dispatch('validate')
                    // this.setStatus('fresh')
                    console.warn('PayPal Checkout: User failed form validation, stopping payment')
                    return
                  }

                  //* Trigger the on_payment_attempted_outputs outputs
                  if (this.component.on_payment_attempted_outputs) {
                    this.dispatch('actions', {
                      detail: {
                        actions: this.component.on_payment_attempted_outputs,
                        component: this.component,
                      },
                    })
                  }

                  if (!doNotStoreComponentDataInCloud(this.component)) {
                    jitsu.track('payment:attempted', {
                      flow_id: this.flowId,
                      payment_service: 'paypal',
                      payment_recurring: false,
                    })

                    window.Savvy.addEventLogs?.([
                      {
                        flow_id: this.flowId,
                        event_type: 'payment:attempted',
                        timestamp: Date.now(),
                        user_data_snapshot: this.userDataValue as UserData,
                        data: {
                          payment_service: 'paypal',
                          payment_recurring: false,
                          component_id: this.component.id,
                          component_key: this.component.key,
                          component_path: [
                            'pages',
                            `${this.pageIndex}`,
                            'components',
                            `${this.componentIndex}`,
                          ],
                        },
                      },
                    ])
                  }

                  const res = actions.order.create({
                    purchase_units: [{ amount: { value: `${this.amountValue || 0}` } }],
                    payer: Object.keys(payer).length > 0 ? payer : undefined,
                  })

                  return res
                } catch (error) {
                  console.error('PayPal Logs: createOrder error:', error)

                  //* Trigger the on_payment_failed_outputs outputs
                  if (this.component.on_payment_failed_outputs) {
                    this.dispatch('actions', {
                      detail: {
                        actions: this.component.on_payment_failed_outputs,
                        component: this.component,
                      },
                    })
                  }
                  return
                }
              }
            : undefined,
        createSubscription:
          this.actionValue === 'subscription'
            ? async (
                data: string,
                actions: {
                  subscription: {
                    create: (arg0: { plan_id: string; payer: {} | undefined }) => string
                  }
                }
              ) => {
                try {
                  //* First, run regular page validation if enforce_validation is true
                  //* If invalid, trigger the regular validation procedure and stop here
                  if (this.component.enforce_validation && !this.isValidValue) {
                    self.dispatch('validate')
                    // this.setStatus('fresh')
                    console.warn('PayPal Checkout: User failed form validation, stopping payment')
                    return
                  }

                  //* Trigger the on_payment_attempted_outputs outputs
                  if (this.component.on_payment_attempted_outputs) {
                    this.dispatch('actions', {
                      detail: {
                        actions: this.component.on_payment_attempted_outputs,
                        component: this.component,
                      },
                    })
                  }

                  if (!doNotStoreComponentDataInCloud(this.component)) {
                    jitsu.track('payment:attempted', {
                      flow_id: this.flowId,
                      payment_service: 'paypal',
                      payment_recurring: true,
                    })
                    window.Savvy.addEventLogs?.([
                      {
                        flow_id: this.flowId,
                        event_type: 'payment:attempted',
                        timestamp: Date.now(),
                        user_data_snapshot: this.userDataValue as UserData,
                        data: {
                          payment_service: 'paypal',
                          payment_recurring: true,
                          component_id: this.component.id,
                          component_key: this.component.key,
                          component_path: [
                            'pages',
                            `${this.pageIndex}`,
                            'components',
                            `${this.componentIndex}`,
                          ],
                        },
                      },
                    ])
                  }

                  const res = actions.subscription.create({
                    plan_id: computedValues(this.planIdValue, this.userDataValue),
                    payer: Object.keys(payer).length > 0 ? payer : undefined,
                  })

                  return res
                } catch (error) {
                  console.error('PayPal Logs: createSubscription error:', error)
                  //* Trigger the on_payment_failed_outputs outputs
                  if (this.component.on_payment_failed_outputs) {
                    this.dispatch('actions', {
                      detail: {
                        actions: this.component.on_payment_failed_outputs,
                        component: this.component,
                      },
                    })
                  }
                  return
                }
              }
            : undefined,

        // Finalize the transaction after payer approval
        onApprove: (
          data: { subscriptionID: string },
          actions: { order: { capture: () => Promise<string> } }
        ) => {
          if (this.actionValue === 'subscription') this.onSuccess(data)
          else
            return actions.order.capture().then((res) => {
              this.onSuccess(data)
            })
        },

        onCancel: () => {
          //* Trigger the on_payment_canceled_outputs outputs
          if (this.component.on_payment_canceled_outputs) {
            this.dispatch('actions', {
              detail: {
                actions: this.component.on_payment_canceled_outputs,
                component: this.component,
              },
            })
          }
        },
        onError: (error: Error) => {
          if (error.message === 'Detected popup close') {
            //* Already handled by onCancel
            return
          }
          //* Trigger the on_payment_failed_outputs outputs
          if (this.component.on_payment_failed_outputs) {
            this.dispatch('actions', {
              detail: {
                actions: this.component.on_payment_failed_outputs,
                component: this.component,
              },
            })
          }
        },
      })
      .render(`#${mountId}`)
  }
}
